import * as preact from "preact";

import readlangClient from "readlangClient";
import * as reportReasons from "shared/reportReasons";
import type { BookOwner } from "stores/BookStore";
import stores from "stores/stores";
import Dialog from "views/Dialog";

type ReportReason = keyof typeof reportReasons.labels;

interface Props {
  bookID: string;
  /** The text's owner, when it's someone the reader may block. */
  owner?: BookOwner;
}

interface State {
  reportReason: ReportReason | null;
  details: string;
  alsoBlock: boolean;
  isSubmitting: boolean;
  error: string | null;
  success: boolean;
}

export default class BookReportDialog extends preact.Component<Props, State> {
  state: State = {
    reportReason: null,
    details: "",
    alsoBlock: false,
    isSubmitting: false,
    error: null,
    success: false,
  };

  handleSubmit = async () => {
    const { reportReason, details, alsoBlock } = this.state;
    if (!reportReason) {
      this.setState({ error: "Please choose a reason" });
      return;
    }

    this.setState({ isSubmitting: true, error: null });
    try {
      await readlangClient.request("POST", "/api/bookFeedback/report", {
        bookID: this.props.bookID,
        reportReason,
        feedbackText: details.trim() || undefined,
      });
      if (alsoBlock && this.props.owner) {
        await stores.user.setBlocked(this.props.owner._id, true);
      }
      this.setState({ success: true, isSubmitting: false });
    } catch (error: any) {
      console.error("Error submitting report:", error);
      this.setState({
        error: error.jqXHR?.alreadyNotified
          ? "Connection problem. Please try again."
          : "Failed to send your report. Please try again.",
        isSubmitting: false,
      });
    }
  };

  render() {
    const { owner } = this.props;
    const { reportReason, details, alsoBlock, isSubmitting, error, success } =
      this.state;

    if (success) {
      return (
        <Dialog
          class="bookReportDialog"
          success={
            <>
              <p>Thanks for your report.</p>
              <p>Readlang reviews reports within 24 hours.</p>
            </>
          }
        />
      );
    }

    return (
      <Dialog
        class="bookReportDialog"
        heading="Report this text"
        error={error}
        actions={
          <button
            class="submitButton"
            onClick={this.handleSubmit}
            disabled={isSubmitting}
          >
            {isSubmitting ? "Sending..." : "Send report"}
          </button>
        }
      >
        <div class="feedbackSection">
          <label>What's the problem?</label>
          {Object.entries(reportReasons.labels).map(([reason, label]) => (
            <label key={reason} class="reportOption">
              <input
                type="radio"
                name="reportReason"
                checked={reportReason === reason}
                onClick={() =>
                  this.setState({ reportReason: reason as ReportReason })
                }
                disabled={isSubmitting}
              />{" "}
              {label}
            </label>
          ))}
        </div>

        <div class="feedbackSection">
          <label>Details (optional)</label>
          <textarea
            value={details}
            onInput={(event) =>
              this.setState({
                details: (event.target as HTMLTextAreaElement).value,
              })
            }
            disabled={isSubmitting}
            rows={3}
          />
        </div>

        {owner && (
          <div class="feedbackSection">
            <label class="reportOption">
              <input
                type="checkbox"
                checked={alsoBlock}
                onClick={(event) =>
                  this.setState({ alsoBlock: event.currentTarget.checked })
                }
                disabled={isSubmitting}
              />{" "}
              Also hide all texts shared by <strong>{owner.username}</strong>
            </label>
          </div>
        )}
      </Dialog>
    );
  }
}
